import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import PasswordInput from "@/components/PasswordInput";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      navigate("/app/rooms", { replace: true });
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <ThemeToggle />
      <div className="hidden md:flex flex-col justify-between relative bg-card border-r border-border p-12 overflow-hidden">
        <div className="absolute inset-0 hero-gradient pointer-events-none" />
        <div className="absolute inset-0 sovereign-grid opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-display text-base font-medium">Sovereign VDR</span>
          </div>
        </div>
        <div className="relative">
          <div className="label-mono text-muted-foreground">— Set a new password</div>
          <h2 className="mt-4 font-display text-4xl tracking-tighter font-medium leading-tight">
            Strong, unique,<br />and only yours.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/login" className="label-mono text-muted-foreground inline-flex items-center gap-2 hover:text-foreground">
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
          <h1 className="mt-6 font-display text-3xl font-medium tracking-tight">New password</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Choose a password with at least 10 characters, including upper, lower, number and symbol.
          </p>

          {!token ? (
            <div className="mt-8 border border-destructive/30 bg-destructive/10 text-destructive p-4 rounded-md text-sm">
              Missing reset token. Request a new reset link from the sign-in page.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="label-mono">New password</label>
                <PasswordInput
                  testId={AUTH.resetPassword}
                  required autoFocus
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="label-mono">Confirm password</label>
                <PasswordInput
                  testId={AUTH.resetConfirm}
                  required
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1"
                />
              </div>
              {err && <div className="text-sm text-destructive">{err}</div>}
              <button
                data-testid={AUTH.resetSubmit}
                type="submit" disabled={loading}
                className="w-full px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
              >
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
